import { Button, Form, Input, Space } from "antd";
import { useEffect } from "react";
import {
  useAddWorkStationMutation,
  useGetWorkStationDetailsQuery,
  useUpdateWorkStationMutation,
} from "../../../features/workStation/workStationApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { ModalComponentProps } from "../../../types";
import EmployeeFileUpload from "../../Employees/EmployeeFileUpload";
import CustomModal from "../../ui/CustomModal";
import Loader from "../../ui/Loader";

export default function ManageWorkStation({
  handleClose,
  type,
  data,
  ...rest
}: ModalComponentProps<string>) {
  const [form] = Form.useForm();
  const {
    isLoading: isWorkStationLoading,
    isError: isWorkStationDetailsError,
    error: workStationDetailsError,
    data: workStationDetails,
  } = useGetWorkStationDetailsQuery(data || "", {
    skip: !data && type !== "UPDATE",
  });
  const [addWorkStation, { isLoading, isError, isSuccess, error }] =
    useAddWorkStationMutation();
  const [
    updateWorkStation,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      isSuccess: isUpdateSuccess,
      error: updateError,
    },
  ] = useUpdateWorkStationMutation();
  // error toast message
  useError(isError, error, "Work station added fail");
  useError(isUpdateError, updateError, "Work station updated fail");
  useError(
    isWorkStationDetailsError,
    workStationDetailsError,
    "Get work station fail"
  );

  const onFinish = (values: unknown) => {
    if (type === "UPDATE" && data) {
      return updateWorkStation({ body: values, id: data });
    }

    return addWorkStation(values);
  };

  useEffect(() => {
    if (Object.keys(workStationDetails?.data || {}) && type === "UPDATE") {
      const workStation = workStationDetails?.data;

      form.setFieldsValue({
        name: workStation?.name,
        abbreviation: workStation?.abbreviation,
        logo: workStation?.logo,
      });
    }
  }, [form, workStationDetails?.data, type]);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Work station added successfully");
      return handleClose();
    }
    if (isUpdateSuccess) {
      toast("success", "Work station updated successfully");
      return handleClose();
    }
  }, [handleClose, isSuccess, isUpdateSuccess]);

  return (
    <CustomModal
      title={`${type === "UPDATE" ? "Update" : "Add New"} Work station`}
      {...rest}
      handleClose={handleClose}
      width={460}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {isWorkStationLoading ? (
          <Loader />
        ) : (
          <>
            <Form.Item
              name="logo"
              label="Logo"
              className="mb-4"
              valuePropName="fileUrl"
            >
              <EmployeeFileUpload
                form={form}
                accept=".png, .jpg, .jpeg, .gif"
                name="logo"
                onChange={(url) =>
                  form.setFieldsValue({
                    logo: url,
                  })
                }
              />
            </Form.Item>
            <Form.Item
              name="name"
              label="Work station name"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter name",
                },
              ]}
            >
              <Input size="large" placeholder="Enter name" />
            </Form.Item>
            <Form.Item
              name="abbreviation"
              label="Abbreviation"
              className="mb-4"
            >
              <Input size="large" placeholder="Ex: HRM" />
            </Form.Item>
          </>
        )}

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
            disabled={isLoading || isUpdateLoading}
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading || isUpdateLoading}
            disabled={isWorkStationLoading}
          >
            {type === "UPDATE" ? "Update" : "Save"}
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}
